/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import debounce from 'lodash.debounce';
import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {IUserPreferences} from '@ion/core/src/preferences/types';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  LogicalPosition
} from '@ion/core/src/types/editors';
import {
  Account,
  CRUDPermissions,
  Entity,
  EntityStatus,
  GenericObject,
  Role,
  User
} from '@ion/core/src/types/entities';
import {
  ApplicationPage
} from '@ion/core/src/types/pages';
import {
  smoothScrollByElement
} from '@ion/core/src/utils/BrowserUtils';
import {
  toDateISOString
} from '@ion/core/src/utils/DateUtil';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  isValidEmail,
  isValidUSAddress,
  isValidUSPhoneNumber
} from '@ion/core/src/utils/Validators';
import {isPossiblePhoneNumber} from '@ion/dls/src/components/phone/Utils';
import {useMergeState} from '@ion/dls/src/hooks/useMergeState';
import {Addresses} from '@ion/features/src/entities/address/Addresses';
import {Applications} from '@ion/features/src/entities/applications/Applications';
import {Contacts} from '@ion/features/src/entities/contacts/Contacts';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {
  FormItem
} from '../components/forms/FormItem';
import {
  FormSection
} from '../components/forms/FormSection';
import {
  FormTextField
} from '../components/forms/FormTextField';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {
  useFormSave
} from '../hooks/useFormSave';
import {
  FormState, useFormState,
  validate
} from '../hooks/useFormState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import {validatePassword} from '../utils/PasswordValidation';
import {CancelButton} from './controls/CancelButton';
import {DatePickerTextConfig} from './datetimepicker/DatePicker';
import DatePicker from './datetimepicker/DatePickerExporter';
import styles, {getClasses} from './editorStyles';
import EntityStatusChooser from './EntityStatusChooser';
import GroupChooser from './GroupChooser';
import LanguageChooser from './LanguageChooser';
import RoleChooser from './RoleChooser';
import {
  getEmptyUserApplication,
  getInitialCtcAdminState,
  getInitialState,
  isValidAddress,
  serialize,
  SerializedArgs,
  TargetType,
  UserEditorFields,
  UserEditorFormState
} from './UserEditorUtils';
import UserPreferencesEditor, {
  PageNameResolver,
  UserPreferencesEditorClient
} from './UserPreferencesEditor';

// defines the props passed down to the user profile editor when editing the current user ...
export interface UserProfileProps {
  preferencesAPI: IUserPreferences;
  applicationPages: ApplicationPage[];
  defaultPageId: string;
  pageNameResolver: PageNameResolver;
  accountsMap: GenericObject;
};

export interface UserEditorProps extends DatePickerTextConfig {
  open: boolean;
  onSave: (user: User) => void;
  onCancel?: () => void;
  user?: User | GenericObject;
  roles?: Role[];
  isCurrentUser?: boolean,
  onSubmit?: Function;
  account: Account;
  userAccount?: Account;
  subAccounts: Account[];
  clear?: boolean;
  loading?: boolean;
  isCtcAdmin?: boolean;
  context?: UserContext;
  includeApplications?: boolean;
  includeAddresses?: boolean;
  includeContacts?: boolean;
  includeGroupProperties?: boolean;
  profileProps?: UserProfileProps;
  namespace: string;
  isViewDialog: boolean;
  includeDateFormat?: boolean;
  multipleAddress?: boolean;
  includeTimeFormat?: boolean;
  onEdit?: Function;
  enableAutoSave?: boolean;
  api?: IUserPreferences;
  usersPermissions?: CRUDPermissions;
}

/**
 * @UserEditor
 *
 * The User Editor with set fields and encapsulated logic for handling the form's state,
 * including validation.
 *
 * Clients of the Editor are simply to provide the necessary props and handle the onSave
 * prop accordingly to create or update a User.
 *
 * * ```typescript
 *
 *    import {UserEditor} from '@ion/dls/src/editors/UserEditor';
 *
 *    <UserEditor open={editorOpen}
 *      account={account}
 *      subAccounts={subAccounts}
 *      clear={clearForm}
 *      loading={loading}
 *      onSave={addNewRole}
 *      onCancel={function() {
 *        setEditorOpen(false);
 *      }} />
 *
 * ```
 *
 * @param {boolean} open Determines if the UserEditor dialog should be open
 * @param {object} account User's account object
 * @param {Array<object>} subAccounts User's sub accounts that get passed to the group chooser
 * @param {Function} onCancel Callback function fired when the dialog requests to be closed
 * @param {Function} onSave Callback function is is invoked when the user clicks submit,
 *                   passing the role as an argument. This function should be used by
 *                   clients for subsequently saving the role to the platform.
 * @param {boolean}  clear If true we clear the form state
 * @param {boolean}  loading disables any for submission while the form is busy
 */
export const UserEditor = ({
  open,
  account,
  onSave,
  onCancel,
  user,
  api,
  roles,
  isCurrentUser,
  profileProps,
  namespace,
  isViewDialog,
  context,
  includeApplications,
  includeAddresses,
  includeContacts,
  userAccount,
  onEdit,
  validDateFormats,
  usersPermissions,
  clear = false,
  loading = false,
  isCtcAdmin = false,
  enableAutoSave = false,
  multipleAddress = false,
  includeDateFormat = false,
  includeTimeFormat = false,
  allowManualDateEntry = false,
  includeGroupProperties = true
}: UserEditorProps) => {
  const editorStyles                                = useExtendStyles(styles);
  const classes                                     = editorStyles();
  const isEditMode                                  = !isObjectEmpty(user);
  const [openDatePickers, setOpenDatePickers]       = useMergeState();
  const [formState, setFormState]                   = useFormState(includeApplications
    ? getInitialCtcAdminState(user, roles, namespace) : getInitialState(user, roles, namespace));
  const [isEditingPassword, setIsEditingPassword]   = useState(false);
  const [currentGroup, setCurrentGroup]             = useState<Account>(null);
  const [passwordError, setPasswordError]           = useState('');
  const [passwordValue, setPasswordValue]           = useState('');
  const [passwordErrorValue, setPasswordErrorValue] = useState(false);
  const [applicationsObj, setApplicationsObj]       = useState<any>(getEmptyUserApplication());
  const [addresses, setAddresses]                   = useState<any>([]);
  const [contactInfo, setContactInfo]               = useState<any>([]);
  const preferencesRef                              = useRef<UserPreferencesEditorClient>();
  const defaultGroupRef                             = useRef(formState[UserEditorFields.Group]?.value);
  const userName                                    = formState[UserEditorFields.Username]?.error;
  const emailAddress                                = formState[UserEditorFields.EmailAddress]?.error;
  const firstName                                   = formState[UserEditorFields.FirstName]?.error;
  const lastName                                    = formState[UserEditorFields.LastName]?.error;
  const phoneNumberValue                            = formState[UserEditorFields.PhoneNumbers]?.value;
  const phoneNumberError                            = formState[UserEditorFields.PhoneNumbers]?.error;
  const addressesValue                              = formState[UserEditorFields.Addresses]?.value;
  const addressesError                              = formState[UserEditorFields.Addresses]?.error;
  const isRootAccount                               = entityId(account) === Number(get('rootAccountId'));
  const isOidcEnabled                               = authMgr.getIsOIDCEnabled();
  const [isSsoUser, setIsSsoUser]                   = useState<boolean>(false);
  const {hasUpdatePermission}                       = usersPermissions || {};

  const submitValidation = {
    [UserEditorFields.EmailAddress] : ({value}) => {
      return validateUser(value);
    },
    [UserEditorFields.Password] : ({value}) => {
      return validatePass(value);
    },
    [UserEditorFields.Applications] : () => {
      let isError = true;
      const tempApplication = applicationsObj?.map((items) => {
        if (items.applications.title === '') {
          items.applications.error = translate('common:features.applications.error');
          isError = false;
        }
        if (items?.roles?.title === '') {
          items.roles.error = translate('common:features.roles.roleError');
          isError = false;
        }
        return items;
      });
      setApplicationsObj(tempApplication);
      return includeApplications ? isError : includeApplications;
    },
    [UserEditorFields.Addresses] : () => {
      let isError = true;
      const tempApplication = addresses?.map((items) => {
        if (items?.addresses.street === '') {
          items.addresses.isStreetError = true;
          isError = false;
        } else {
          items.addresses.isStreetError = false;
        }
        if (items?.addresses.city === '') {
          items.addresses.isCityError = true;
          isError = false;
        } else {
          items.addresses.isCityError = false;
        }
        if (items?.addresses.stateProvince === '' || items?.addresses.stateProvince === 'State') {
          items.addresses.isStateError = true;
          isError = false;
        } else {
          items.addresses.isStateError = false;
        }
        if (items?.addresses.country === '') {
          items.addresses.isCountryError = true;
          isError = false;
        } else {
          items.addresses.isCountryError = false;
        }
        if (items?.addresses.zip === '') {
          items.addresses.isZipError = true;
          isError = false;
        } else {
          items.addresses.isZipError = false;
        }
        const {countryCd, zip} = items?.addresses || {};
        if (countryCd === 'US') {
          const isValidAddress = isValidUSAddress(countryCd, zip);
          items.addresses.isZipInvalidError = isValidAddress === false;
          if (!isValidAddress) {
            isError = false;
          }
        } else {
          items.addresses.isZipError = false;
        }
        return items;
      });
      setAddresses(tempApplication);
      return includeAddresses ? isError : includeAddresses;
    },
    [UserEditorFields.PhoneNumbers] : () => {
      let isError = true;
      const tempContact = contactInfo?.map((items) => {
        const phoneNumber     = items?.phoneNumbers.phoneNumber;
        if (!phoneNumber) {
          items.phoneNumbers.PhoneError = true;
          isError = false;
        } else {
          items.phoneNumbers.PhoneError = false;
        }
        if (phoneNumber && (isCtcAdmin ? !isPossiblePhoneNumber(phoneNumber) : !isValidUSPhoneNumber(phoneNumber))) {
          items.phoneNumbers.isPhoneNumError = true;
          isError = false;
        } else {
          items.phoneNumbers.isPhoneNumError = false;
        }
        return items;
      });
      setContactInfo(tempContact);
      return includeContacts ? isError : includeContacts;
    }
  };

  const serializer = function(state: FormState): GenericObject {
    const current = state as UserEditorFormState;
    const options = {
      state : {...current, users: account},
      api   : enableAutoSave ? api : profileProps?.preferencesAPI,
      userAccount,
      isCurrentUser,
      enableAutoSave,
      roles,
      namespace
    } as SerializedArgs;
    if (isCurrentUser) {
      options.preferences = preferencesRef.current?.getPreferences();
    }
    if (isOidcEnabled) {
      options.state.isSsoUser = isSsoUser;
    }
    return serialize(options);
  };

  const onSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    formState,
    setFormState,
    onSave
  });

  function handleSubmit(mode: string) {
    return function() {
      const isEdit = mode === 'edit';
      onEdit(isEdit);
    };
  }

  // defines the user translation interpolation options ...
  const translations = {entityLabel: translate('common:entityLabels.user')};

  // when the clear prop is set to true, we clear all form state and subsequent fields ...
  useEffect(() => {
    if (isCtcAdmin) {
      if (formState?.addresses) {
        const addressesLevel = [];
        formState?.addresses?.value?.map((value, index) => {
          addressesLevel.push({
            id        : index + 1,
            addresses : {
              ...value,
              isStreetError     : false,
              isStateError      : false,
              isCityError       : false,
              isZipError        : false,
              isZipInvalidError : false,
              isCountryError    : false
            }
          });
        });
        setAddresses(addressesLevel);
      }
      if (formState?.phoneNumbers) {
        const contactLevel = [];
        formState?.phoneNumbers?.value?.map((value, index) => {
          contactLevel.push({
            id           : index + 1,
            phoneNumbers : {
              ...value,
              isPhoneNumError     : false,
              PhoneError          : false,
              isExtensionNumError : false
            }
          });
        });
        setContactInfo(contactLevel);
      }
    }
    clear && clearForm();
  }, [clear, isCtcAdmin]);

  // clears the state of the form and all related fields ...
  function clearForm() {
    setFormState({}, true);
  };

  if (userName || emailAddress || firstName || lastName) {
    smoothScrollByElement('editor');
  } else if (isValidAddress({phoneNumberValue, phoneNumberError, addressesValue, addressesError})) {
    smoothScrollByElement('editor', LogicalPosition.End);
  }

  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value} = target;
    const required = target.required || false;
    let error;
    if (name === UserEditorFields.EmailAddress) {
      error = !validate(value, isValidEmail);
    } else if (name === UserEditorFields.ConfirmPassword) {
      error = !(value === formState[UserEditorFields.Password]?.value);
      value === '' ? error = false : null;
    }
    setFormState({[name]: {name, value, required, error}});
  }, 400);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    debounceFieldChange(e.target);
  };

  const getAccountId = useCallback(() => (
    entityId(isCtcAdmin ? account : currentGroup)
  ), [currentGroup, account, isCtcAdmin]);

  // On mount, when editing a user, we must check if the user being edited is an SSO user.
  // If it is, the email field should be disabled.
  useEffect(() => {
    const username = formState[UserEditorFields.Username]?.value;
    if (isOidcEnabled && !isViewDialog && username) {
      validateUsername(username, getAccountId());
    }
  }, []);

  async function validateUsername(value: string, accountId: string) {
    if (isOidcEnabled && value) {
      let name = UserEditorFields.Username;
      const {isSso, error} = await authMgr.getOidcConfig(value, accountId);
      setIsSsoUser(isSso);
      setFormState({[name]: {name, value, required: true, error}});

      name = UserEditorFields.EmailAddress;
      if (error) {
        setFormState({[name]: {name, value: '', required: true, error}});
      } else if (isSso) {
        setFormState({[name]: {name, value, required: true, error}});
      }
    }
  }

  function handleUsernameChange(e: ChangeEvent<HTMLInputElement>) {
    const {value}   = e?.target || {};
    const accountId = getAccountId();
    validateUsername(value, accountId);
  };

  function handleEmailChange(e: ChangeEvent<HTMLInputElement>) {
    e?.persist();
    const {name, required, value} = e?.target;
    setFormState({[name]: {name, value, required, error: false}});
    debounceFieldChange(e?.target);
  };

  function handlePasswordChange(e: ChangeEvent<HTMLInputElement>) {
    const {name, value} = e.target;
    const required = true;
    let error = !isPassswordValid(value);
    value === '' ? error = false : null;
    setPasswordValue(value);
    setPasswordErrorValue(error);
    setFormState({[name]: {name, value, required, error}});
  }

  // invoked when a stand-alone, chooser specific component change is made ...
  function onChooserFieldChange({name, value, required = false}: TargetType) {
    setFormState({[name]: {name, value, required}});
  };

  useEffect(() => {
    if (currentGroup && currentGroup !== defaultGroupRef.current) {
      setFormState({
        [UserEditorFields.Role] : {
          name     : UserEditorFields.Role,
          value    : null,
          required : true
        }
      });
    }
  }, [currentGroup]);

  function isPassswordValid(password) {
    const userValues = {
      username  : user.username,
      groupName : user.group,
      config    : {
        passwordIsPhrase : {
          isPhrase : isCurrentUser
        }
      }
    };
    const validationResults = validatePassword(password, userValues);
    setPasswordError(validationResults);
    return validationResults === undefined;
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value: toDateISOString(value), required}});
  };

  function onChangePassword() {
    if (isEditingPassword) {
      setPasswordValue('');
      setPasswordErrorValue(false);
      setField({name: UserEditorFields.Password, value: '', required: false, error: false});
      setField({name: UserEditorFields.ConfirmPassword, value: '', required: false, error: false});
    } else {
      setField({name: UserEditorFields.ConfirmPassword, value: '', required: false, error: false});
    }
    setIsEditingPassword(!isEditingPassword);
  }

  // validation for email when form submitted
  const validateUser = (value) => {
    return isValidEmail(value);
  };

  // validation for password when form submitted
  const validatePass = (value) => {
    const userValues = {
      username  : user.username,
      groupName : user.group
    };

    const validatePassResult = validatePassword(value, userValues);
    const confirmPasswordResult = formState[UserEditorFields.ConfirmPassword]?.error;
    return !!(validatePassResult === undefined && confirmPasswordResult === false);
  };

  function setField({name, value, required, error}) {
    setFormState({[name]: {name, value, required, error}});
  };

  const getTitle = (isCurrentUser: boolean, isEditMode: boolean, isViewDialog: boolean) => {
    if (!isCurrentUser) {
      return translate(`common:editors.shared.${isEditMode ? (isViewDialog ? 'view' : 'edit') : 'add'}`, translations);
    }
    return translate('common:editors.userEditor.profile');
  };

  return (
    <Dialog open={open}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCancel} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {getTitle(isCurrentUser, isEditMode, isViewDialog)}
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div id={'editor'} className={isCtcAdmin ? classes.formWrapperCtc : classes.formWrapper}>
          <DialogContent className={classes.formDialogContent}>
            <FormSection title={translate('common:editors.shared.details', translations)}
              legend={translate('common:editors.shared.fieldsRequired')}
              bottomBorder>
              <>
                <FormItem>
                  <FormTextField required
                    disabled={isViewDialog || false}
                    fieldLabel={translate('common:editors.username.label')}
                    placeholder={translate('common:editors.username.placeholder')}
                    name={UserEditorFields.Username}
                    maxLength={64}
                    onChange={handleFieldChange}
                    onBlur={handleUsernameChange}
                    hasError={formState[UserEditorFields.Username]?.error}
                    errorText={translate('admin-users:form.errors.userDetails.username')}
                    defaultValue={formState[UserEditorFields.Username]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField required
                    disabled={isSsoUser || isViewDialog}
                    fieldLabel={translate('common:editors.emailAddress.label')}
                    placeholder={translate('common:editors.emailAddress.placeholder')}
                    name={UserEditorFields.EmailAddress}
                    maxLength={64}
                    onChange={handleEmailChange}
                    hasError={formState[UserEditorFields.EmailAddress]?.error}
                    errorText={translate('common:editors.emailAddress.error')}
                    value={formState[UserEditorFields.EmailAddress]?.value} />
                </FormItem>
                {!includeApplications && <FormItem>
                  <GroupChooser required
                    account={account}
                    readOnly={isCurrentUser || isViewDialog}
                    useDisabledStyles={isCurrentUser || isViewDialog}
                    onChange={function(value) {
                      onChooserFieldChange({
                        name     : UserEditorFields.Group,
                        required : true,
                        value
                      });
                      setCurrentGroup(value);
                      if (value) {
                        validateUsername(formState[UserEditorFields.Username]?.value, entityId(value));
                      }
                    }}
                    hasError={formState[UserEditorFields.Group]?.error}
                    defaultValue={formState[UserEditorFields.Group]?.value || account} />
                </FormItem>}
                {!includeApplications && <FormItem>
                  <RoleChooser required
                    readOnly={isCurrentUser || isViewDialog}
                    useDisabledStyles={isCurrentUser}
                    accountId={entityId(account)}
                    appKey={get('appKey')}
                    currentGroup={currentGroup}
                    defaultGroup={defaultGroupRef.current}
                    onChange={function(value) {
                      onChooserFieldChange({
                        name     : UserEditorFields.Role,
                        value,
                        required : true
                      });
                    }}
                    hasError={formState[UserEditorFields.Role]?.error}
                    defaultValue={formState[UserEditorFields.Role]?.value} />
                </FormItem>
                }
                <FormItem>
                  <FormTextField required
                    disabled={isViewDialog || false}
                    fieldLabel={translate('common:editors.firstName.label')}
                    placeholder={translate('common:editors.firstName.placeholder')}
                    name={UserEditorFields.FirstName}
                    maxLength={64}
                    onChange={handleFieldChange}
                    hasError={formState[UserEditorFields.FirstName]?.error}
                    errorText={translate('common:editors.shared.required')}
                    defaultValue={formState[UserEditorFields.FirstName]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField required
                    disabled={isViewDialog || false}
                    fieldLabel={translate('common:editors.lastName.label')}
                    placeholder={translate('common:editors.lastName.placeholder')}
                    name={UserEditorFields.LastName}
                    maxLength={64}
                    onChange={handleFieldChange}
                    hasError={formState[UserEditorFields.LastName]?.error}
                    errorText={translate('common:editors.shared.required')}
                    defaultValue={formState[UserEditorFields.LastName]?.value} />
                </FormItem>
                {!isCurrentUser && (
                  <>
                    <FormItem>
                      <EntityStatusChooser entity={Entity.user}
                        readOnly={isViewDialog || false}
                        onChange={function(value) {
                          onChooserFieldChange({
                            name : UserEditorFields.Status,
                            value
                          });
                        }}
                        status={formState[UserEditorFields.Status]?.value || EntityStatus.Enabled} />
                    </FormItem>
                    <FormItem className={classes.datePickerItem}>
                      <DatePicker closeOnClickOutside
                        useDatePickerTextField
                        manualInput={allowManualDateEntry}
                        isDatePicker={allowManualDateEntry}
                        allowManualEntry={allowManualDateEntry}
                        validDateTimeFormats={validDateFormats}
                        disabled={isViewDialog || false}
                        fieldLabel={translate('common:editors.endDate.label')}
                        placeholder={translate('common:editors.endDate.placeholder')}
                        open={openDatePickers[UserEditorFields.EndDate]}
                        hideClearButton={isCtcAdmin}
                        toggle={function() {
                          setOpenDatePickers(
                            {[UserEditorFields.EndDate]: !openDatePickers[UserEditorFields.EndDate]}
                          );
                        }}
                        onChange={function(value, required) {
                          handleDateChange({
                            name : UserEditorFields.EndDate,
                            value,
                            required
                          });
                        }}
                        defaultValue={formState[UserEditorFields.EndDate]?.value} />
                    </FormItem>
                  </>
                )}
                {(isEditMode && !isViewDialog && !isSsoUser) &&
                  <FormItem>
                    <FormTextField autoComplete='new-password' fieldLabel={translate('common:editors.password.label')}
                      placeholder={translate('common:editors.password.placeholder')}
                      name={UserEditorFields.Password}
                      maxLength={64}
                      type='password'
                      onChange={handlePasswordChange}
                      hasError={passwordErrorValue}
                      errorText={passwordError}
                      value={passwordValue}
                      InputProps={{
                        endAdornment : (
                          <TertiaryButton style={{marginRight: '10px'}} onClick={onChangePassword} usecaactive={1}>
                            {isEditingPassword ? translate('common:editors.password.cancel')
                              : translate('common:editors.password.change')}
                          </TertiaryButton>
                        )
                      }} />
                  </FormItem>}
                {isEditingPassword &&
                  <FormItem>
                    <FormTextField fieldLabel={translate('common:editors.confirmPassword.label')}
                      placeholder={translate('common:editors.confirmPassword.placeholder')}
                      name={UserEditorFields.ConfirmPassword}
                      maxLength={64}
                      type='password'
                      onChange={handleFieldChange}
                      hasError={formState[UserEditorFields.ConfirmPassword]?.error}
                      errorText={translate('common:editors.passwordMatch.msg')}
                      defaultValue={formState[UserEditorFields.ConfirmPassword]?.value} />
                  </FormItem>}
                {!isCurrentUser && (
                  <FormItem>
                    <LanguageChooser useForm
                      useGroupDefault
                      readOnly={isViewDialog}
                      onChange={function(value) {
                        onChooserFieldChange({
                          name : UserEditorFields.Language,
                          value
                        });
                      }}
                      defaultValue={formState[UserEditorFields.Language]?.value} />
                    <span className={classes.infoLabel}>
                      {translate('common:editors.preferences.language.info')}
                    </span>
                  </FormItem>
                )}
              </>
            </FormSection>
            {includeApplications && <Applications required
              context={context}
              accountId={entityId(account)}
              roles={roles}
              formState={formState}
              applications={applicationsObj}
              isCurrentUser={isCurrentUser}
              readOnly={isCurrentUser || isViewDialog}
              isRootAccount={isRootAccount}
              vbusRoles={get('vbusRoles')}
              onChange={function(value) {
                setApplicationsObj(value);
                onChooserFieldChange({
                  name     : UserEditorFields.Applications,
                  required : true,
                  value    : value
                });
              }} />
            }
            {(isCurrentUser || profileProps) && (
              <UserPreferencesEditor ref={preferencesRef}
                includeDateFormat={includeDateFormat}
                includeGroupProperties={includeGroupProperties}
                ctcAdmin ={isCtcAdmin}
                includeTimeFormat={includeTimeFormat}
                bottomBorder={isCtcAdmin}
                api={profileProps.preferencesAPI}
                defaultPageId={profileProps.defaultPageId}
                pages={profileProps.applicationPages}
                pageNameResolver={profileProps.pageNameResolver}
                accountsMap={profileProps.accountsMap}
                account={account}
                user={user}
                omitPreferences={isCtcAdmin} />
            )}
            {includeContacts && <Contacts required
              isCtcAdmin={isCtcAdmin}
              contactInfo={contactInfo}
              readOnly={isViewDialog}
              phoneNumbers={phoneNumberValue}
              isPhoneNumberError={phoneNumberError}
              onChange={function(value) {
                const phoneNumber = value?.map((item) => item.phoneNumbers);
                const finalContact = phoneNumber.map(({
                  phoneNumber,
                  extension,
                  phoneType,
                  isPhoneNumError,
                  PhoneError,
                  isExtensionNumError
                }) => {
                  return {
                    phoneNumber,
                    extension,
                    phoneType : phoneType?.toUpperCase(),
                    isPhoneNumError,
                    PhoneError,
                    isExtensionNumError
                  };
                });
                setContactInfo(value);
                onChooserFieldChange({
                  name     : UserEditorFields.PhoneNumbers,
                  required : false,
                  value    : finalContact
                });
              }} />
            }
            {includeAddresses && <Addresses required
              addresses={addresses}
              readOnly={isViewDialog}
              multipleAddress={multipleAddress}
              bottomBorder={false}
              onChange={function(value) {
                const addresses = value?.map((item) => item.addresses);
                const finalAddress = addresses.map(({
                  street,
                  street2,
                  city,
                  stateProvince,
                  stateProvinceCd,
                  zip,
                  country,
                  countryCd,
                  addressType,
                  isStreetError,
                  isStateError,
                  isCityError,
                  isZipError,
                  isZipInvalidError
                }) => {
                  return {
                    street,
                    street2,
                    city,
                    stateProvince,
                    stateProvinceCd,
                    zip,
                    country,
                    countryCd,
                    addressType : addressType?.toUpperCase(),
                    isStreetError,
                    isStateError,
                    isCityError,
                    isZipError,
                    isZipInvalidError
                  };
                });
                setAddresses(value);
                onChooserFieldChange({
                  name     : UserEditorFields.Addresses,
                  required : false,
                  value    : finalAddress
                });
              }} />
            }
          </DialogContent>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        {!isViewDialog && <TertiaryButton disabled={loading}
          onClick={function(e) {
            clearForm();
            onCancel();
          }}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>}
        {!isViewDialog && <div className={classes.primaryButton}>
          <PrimaryButton disabled={loading} fullWidth type='submit' onClick={function(e) {
            onSubmit(e);
          }}>
            {
              loading ? (
                <ButtonSpinner />
              ) : (
                <>{translate('common:editors.shared.done')}</>
              )
            }
          </PrimaryButton>
        </div>}
      </DialogActions>
      {isViewDialog && <DialogActions>
        {onCancel && <CancelButton onCancel={onCancel} />}
        <PrimaryButton onClick={handleSubmit('audit')}>
          {translate('common:audit')}
        </PrimaryButton>
        <PrimaryButton onClick={handleSubmit('edit')}
          disabled={!hasUpdatePermission}>
          {translate('common:edit')}
        </PrimaryButton>
      </DialogActions>}
    </Dialog>
  );
};

export default UserEditor;
